import type { ManuscriptJsonb } from '@xdocs/schemas';

/**
 * F67 offline draft mirror (E20 §5.10-3: "editor content — autosave to
 * `document_versions` over WS + local draft mirror; conflict on reopen →
 * 'restore draft?' prompt naming the document, never silent clobber").
 * `editor.save` is the server-authoritative write; this is the local durable
 * copy of in-flight (not-yet-persisted) edits so a tab close, crash, or
 * extended offline stretch never silently loses keystrokes.
 *
 * Raw IndexedDB (no wrapper lib — not on the approved-libs allowlist and
 * this is a ~30-line surface), mirroring `features/reader/readerIndexedDb.ts`
 * exactly (same DB-per-feature convention, same fail-soft contract): every
 * method fails soft (resolves `undefined`/no-throw) in non-browser/
 * unsupported environments (SSR, older browsers, private-mode storage
 * denial) so a session without IndexedDB simply loses local-draft-mirror
 * safety, never crashes — the live `editor.save` autosave path is unaffected.
 */
export interface StoredEditorDraft {
  documentId: string;
  /** The version this draft branched from when it was last written locally. */
  baseVersionId: string;
  content: ManuscriptJsonb;
  updatedAt: string;
}

const DB_NAME = 'xdocs-editor';
const DB_VERSION = 1;
const STORE_NAME = 'drafts';

function isIndexedDbAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

function openDb(): Promise<IDBDatabase | null> {
  if (!isIndexedDbAvailable()) return Promise.resolve(null);
  return new Promise((resolve) => {
    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'documentId' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

export async function getStoredEditorDraft(documentId: string): Promise<StoredEditorDraft | undefined> {
  const db = await openDb();
  if (!db) return undefined;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const request = tx.objectStore(STORE_NAME).get(documentId);
      request.onsuccess = () => resolve((request.result as StoredEditorDraft | undefined) ?? undefined);
      request.onerror = () => resolve(undefined);
    } catch {
      resolve(undefined);
    } finally {
      db.close();
    }
  });
}

export async function setStoredEditorDraft(entry: StoredEditorDraft): Promise<void> {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(entry);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    } finally {
      db.close();
    }
  });
}

export async function clearStoredEditorDraft(documentId: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(documentId);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    } finally {
      db.close();
    }
  });
}
